import { User } from 'oidc-client-ts';
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import services from 'services';
import { AuthManagerInterface, AuthMode } from 'types';
import { convertExceptionToJson, getMainConfig, logger } from 'utils';
import ErrorView from 'view/components/ErrorView/ErrorView';
import FullScreenLoader from 'view/components/FullScreenLoader/FullScreenLoader';

import { useError } from '../error';

import useSessionExpired from './useSessionExpired';

interface UserContextValue {
  user: User | null;
  sessionIsExpired: boolean;
}

interface UserProviderState {
  initialized: boolean;
  user: User | null;
}

export const UserContext = createContext<UserContextValue | null>(null);

const querySessionStatus = async (authManager: AuthManagerInterface) => {
  try {
    return (await authManager.getUserManager()?.querySessionStatus()) ?? null;
  } catch (errorQuerySession: unknown) {
    logger.warn('Error querying session status during auth manager initialization', errorQuerySession);
    return null;
  }
};

const UserProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [{ user, initialized }, setState] = useState<UserProviderState>({
    initialized: services.appFrame.auth.getAuthMode() === AuthMode.NoLogin,
    user: null,
  });

  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    const oidcConfig = getMainConfig('oidc');
    const authManager = services.appFrame.auth.createAuthManager(oidcConfig, null);
    if (!authManager) {
      setState({
        user: null,
        initialized: true,
      });
      return undefined;
    }

    const unsubscribeOnUserChange = authManager.onUserChange((newUser: User | null) => {
      setState((prevState) => ({
        ...prevState,
        user: newUser,
      }));
    });

    const initAuthManager = async () => {
      try {
        await authManager.initialize();
      } catch (errorInit: unknown) {
        const sessionStatus = (await querySessionStatus(authManager)) || undefined;

        logger.error('Error initializing root auth manager!', errorInit);
        services.appFrame.error.main.addError({
          title: 'errors.auth_init_error.title',
          message: 'errors.common_message',
          details: {
            type: 'AUTH_INIT_ERROR',
            timestamp: new Date(),
            error: convertExceptionToJson(errorInit),
          },
          actions: sessionStatus && [
            {
              id: 'signout',
              text: 'errors.auth_init_error.actions.logout',
              onClick: () => {
                services.appFrame.auth.logout();
              },
            },
          ],
        });
        return;
      }

      try {
        await services.appFrame.config.loadFeatures();
      } catch (error) {
        logger.error('Unable to load module features!', error);
        services.appFrame.error.main.addError({
          title: 'errors.load_error.title.module_feature_config',
          message: 'errors.common_message',
          details: {
            type: 'MODULE_FEATURE_LOAD_ERROR',
            timestamp: new Date(),
            error: convertExceptionToJson(error),
          },
        });
        return;
      }

      setTimeout(() => {
        setState({
          user: services.appFrame.auth.getUser(),
          initialized: true,
        });
      });
    };

    initAuthManager();

    return () => {
      unsubscribeOnUserChange();
      services.appFrame.auth.destroyAuthManager();
    };
  }, []);

  const sessionIsExpired = useSessionExpired();
  const ctx: UserContextValue = useMemo(
    () => ({
      user,
      sessionIsExpired,
    }),
    [user, sessionIsExpired],
  );

  const { main: error } = useError();
  if (!initialized) {
    return error ? <ErrorView {...error} /> : <FullScreenLoader text={t('progress.authenticating')} />;
  }

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within a UserProvider!');
  }

  return ctx;
};

export default UserProvider;
